import { useMemo } from "react";

import { ExclamationTriangleIcon, Squares2X2Icon } from "@heroicons/react/24/outline";

import useListingStats from "../../../data/useListingStats";
import PriceFormat from "../../../utils/priceFormat";
import classNames from "../../../utils/classNames";
import { IListingStatsByCategory } from "../../../models/Part";

const MISSING_FIELDS: { key: string; label: string }[] = [
  { key: "MPN", label: "MPN" },
  { key: "OEMPN", label: "OE Part Number" },
  { key: "Brand", label: "Brand" },
  { key: "Make", label: "Make" },
  { key: "Model", label: "Model" },
  { key: "VIN", label: "VIN" },
  { key: "Grade", label: "Grade" },
  { key: "KType", label: "KType" },
];

/** "By category" + "Missing fields" panels, rendered in the new
 *  Marketplace Insights visual language. Shares the listing stats
 *  query cache with the main eBay listings table. */
export default function MarketplaceBreakdown() {
  const { listingStats } = useListingStats();
  const stats = listingStats.data?.data;

  const byCategory = useMemo<IListingStatsByCategory[]>(
    () =>
      (stats?.byCategory ?? [])
        .slice()
        .sort((a, b) => (b.totalActive ?? 0) - (a.totalActive ?? 0)),
    [stats?.byCategory]
  );

  const maxActive = byCategory.reduce(
    (max, r) => Math.max(max, r.totalActive ?? 0),
    0
  );

  const missingRows = useMemo(() => {
    if (!stats) return [] as { key: string; label: string; count: number }[];
    return MISSING_FIELDS.map((f) => ({
      ...f,
      count: stats.listingsMissing?.[f.key] ?? 0,
    })).sort((a, b) => b.count - a.count);
  }, [stats]);

  const totalMissing = missingRows.reduce((s, r) => s + r.count, 0);
  const totalActive = stats?.totalActive ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      {/* By category */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm xl:col-span-2">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Squares2X2Icon className="h-4 w-4 text-sky-600" />
              Performance by category
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Which categories are driving your active listings and their
              aging profile.
            </p>
          </div>
          {totalActive > 0 && (
            <span className="text-xs text-slate-500">
              {totalActive.toLocaleString()} active · {byCategory.length}{" "}
              categories
            </span>
          )}
        </div>

        <div className="max-h-[22rem] overflow-y-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2 text-right">Active</th>
                <th className="px-3 py-2 text-right">Over 30d</th>
                <th className="px-3 py-2 text-right">Avg age</th>
                <th className="px-3 py-2 text-right">Avg price</th>
                <th className="px-3 py-2 text-right">Total value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {byCategory.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-sm italic text-slate-500"
                  >
                    No category data available yet.
                  </td>
                </tr>
              ) : (
                byCategory.map((row) => {
                  const pct =
                    maxActive > 0
                      ? Math.round(((row.totalActive ?? 0) / maxActive) * 100)
                      : 0;
                  const stalePct =
                    (row.totalActive ?? 0) > 0
                      ? Math.round(((row.over30DaysOld ?? 0) / row.totalActive!) * 100)
                      : 0;
                  return (
                    <tr
                      key={row.category?.id ?? row.category?.name}
                      className="text-sm transition-colors hover:bg-slate-50"
                    >
                      <td className="px-3 py-2 font-medium text-slate-900">
                        <div className="flex items-center gap-2">
                          <span className="truncate" title={row.category?.name}>
                            {row.category?.name || "Unknown"}
                          </span>
                        </div>
                        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-sky-400 to-primary-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-sky-700">
                        {(row.totalActive ?? 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <span
                          className={classNames(
                            "inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                            stalePct >= 50
                              ? "bg-rose-50 text-rose-700 ring-rose-200"
                              : stalePct >= 25
                                ? "bg-amber-50 text-amber-700 ring-amber-200"
                                : "bg-emerald-50 text-emerald-700 ring-emerald-200"
                          )}
                        >
                          {(row.over30DaysOld ?? 0).toLocaleString()} · {stalePct}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {Math.round(row.averageAge ?? 0)}d
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {PriceFormat(row.averagePrice)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                        {PriceFormat(row.totalPrice)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Missing fields */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
              Missing listing fields
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Gaps that hurt discoverability — fix these to win more eBay
              search impressions.
            </p>
          </div>
          {totalMissing > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200">
              {totalMissing.toLocaleString()} gaps
            </span>
          )}
        </div>

        <ul className="divide-y divide-slate-100">
          {missingRows.map((row) => {
            const pct = totalActive > 0
              ? Math.round((row.count / totalActive) * 100)
              : 0;
            const hasGap = row.count > 0;
            return (
              <li
                key={row.key}
                className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{row.label}</span>
                    <span
                      className={classNames(
                        "font-semibold tabular-nums",
                        hasGap ? "text-amber-700" : "text-slate-400"
                      )}
                    >
                      {row.count.toLocaleString()}
                      {hasGap && (
                        <span className="ml-1 text-[11px] font-medium text-slate-500">
                          · {pct}%
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={classNames(
                        "h-full rounded-full",
                        hasGap
                          ? "bg-gradient-to-r from-amber-400 to-rose-500"
                          : "bg-emerald-400"
                      )}
                      style={{ width: `${hasGap ? Math.max(pct, 4) : 100}%` }}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
